import { TOOL_BELT_SLOTS } from "odyssey-shared";
import { say } from "../../words.ts";

/**
 * The actions a key can be bound to (P1.9-T50; ADR-061's amendments "key rebinding", decisions 1 and 2, "Move joins
 * the key table", points 1 and 2, and "every key rebindable but Escape", P1.9-T174): each one's id and its default
 * physical key, in the order Controls lists them, and Controls' rows. The name Controls shows an action by is the
 * language files' (`controls.actions.*` and `controls.ability` in `odyssey-lang`).
 */

/**
 * The start of a code no keyboard sends.
 */
const UNBOUND_PREFIX = "Unbound";

/**
 * How many ability slots have a key.
 */
export const ABILITY_ACTIONS = 10;

/**
 * An ability slot's action, by its place from 1.
 */
export type AbilityActionId = "ability1" | "ability2" | "ability3" | "ability4" | "ability5" | "ability6" | "ability7" | "ability8" | "ability9" | "ability10";

/**
 * How many tool belt slots have an action.
 */
export const TOOL_ACTIONS = TOOL_BELT_SLOTS;

/**
 * A tool belt slot's action, by its place from 1.
 */
export type ToolActionId = "tool1" | "tool2" | "tool3" | "tool4";

/**
 * A direction of Move: the four of the first set (W, A, S, D) and the four of the second (the arrows).
 */
export type MoveActionId = "moveUp" | "moveLeft" | "moveDown" | "moveRight" | "moveUpAlt" | "moveLeftAlt" | "moveDownAlt" | "moveRightAlt";

/**
 * An action a key can be bound to.
 */
export type ActionId = MoveActionId | "interact" | "run" | "jump" | AbilityActionId | "swapPage" | "target" | "stance" | "toolCycle" | ToolActionId | "companion" | "openChat" | "inventory" | "characterSheet" | "worldMap" | "flag" | "emotes" | "itemNames" | "performance" | "collision";

/**
 * One action: its id and the physical key (`KeyboardEvent.code`) it has before a player chooses.
 */
export interface ActionSpec {
  readonly id: ActionId;
  readonly code: string;
}

/**
 * The ability slots' default keys in slot order: 1 to 9, then 0 for the tenth (the author's ruling of 2026-10-02).
 */
const ABILITY_DEFAULTS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "0"] as const;

/**
 * The ability slots' actions, in slot order.
 */
const ABILITY_SPECS: readonly ActionSpec[] = ABILITY_DEFAULTS.map((digit, slot) => ({ id: `ability${slot + 1}` as AbilityActionId, code: `Digit${digit}` }));

/**
 * The code of an action that has no key until a player chooses one (ADR-142 decision 3b: Tool 1 to Tool 4). It is shaped
 * as a physical key's code but no keyboard sends it, so such an action is on no key, holds no key twice with another, and
 * is shown as Unbound. Choosing a key for another action that one of them holds hands it this code, as any swap does.
 *
 * @param place The action's place from 1.
 * @returns The code.
 */
export function unboundCode(place: number): string {
  return `${UNBOUND_PREFIX}${place}`;
}

/**
 * Whether a code is one no keyboard sends, an action's place of no key (`unboundCode`).
 *
 * @param code The code.
 * @returns True for an unbound action's code.
 */
export function isUnboundCode(code: string): boolean {
  return code.startsWith(UNBOUND_PREFIX);
}

/**
 * The tool belt slots' actions, in slot order, unbound until a player chooses a key.
 */
const TOOL_SPECS: readonly ActionSpec[] = Array.from({ length: TOOL_ACTIONS }, (_, slot) => ({ id: `tool${slot + 1}` as ToolActionId, code: unboundCode(slot + 1) }));

/**
 * Move's directions in the order Controls shows their caps on its rows: W, A, S and D by their place on the keyboard,
 * then the arrows, the second set (P1.9-T174).
 */
const MOVE_SPECS: readonly ActionSpec[] = [
  { id: "moveUp", code: "KeyW" },
  { id: "moveLeft", code: "KeyA" },
  { id: "moveDown", code: "KeyS" },
  { id: "moveRight", code: "KeyD" },
  { id: "moveUpAlt", code: "ArrowUp" },
  { id: "moveLeftAlt", code: "ArrowLeft" },
  { id: "moveDownAlt", code: "ArrowDown" },
  { id: "moveRightAlt", code: "ArrowRight" },
];

/**
 * Move's directions, both sets.
 */
export const MOVE_ACTIONS: readonly MoveActionId[] = MOVE_SPECS.map((spec) => spec.id as MoveActionId);

/**
 * Each set of Move's directions, in cap order: up, left, down, right.
 */
export const MOVE_SETS: Readonly<Record<"keys" | "arrows", readonly MoveActionId[]>> = { keys: MOVE_ACTIONS.slice(0, 4), arrows: MOVE_ACTIONS.slice(4) };

const MOVE_SET: ReadonlySet<ActionId> = new Set(MOVE_ACTIONS);

/**
 * Whether an action is a direction of Move.
 *
 * @param id The action.
 * @returns True for every direction of both sets.
 */
export function isMove(id: ActionId): id is MoveActionId {
  return MOVE_SET.has(id);
}

/**
 * Every action, in the order Controls lists them.
 */
export const ACTIONS: readonly ActionSpec[] = [
  ...MOVE_SPECS,
  { id: "interact", code: "KeyE" },
  { id: "run", code: "ShiftLeft" },
  { id: "jump", code: "Space" },
  ...ABILITY_SPECS,
  { id: "swapPage", code: "Backquote" },
  { id: "target", code: "Tab" },
  { id: "stance", code: "KeyR" },
  { id: "toolCycle", code: "KeyQ" },
  ...TOOL_SPECS,
  { id: "companion", code: unboundCode(TOOL_ACTIONS + 1) },
  { id: "openChat", code: "Enter" },
  { id: "inventory", code: "KeyI" },
  { id: "characterSheet", code: "KeyC" },
  { id: "worldMap", code: "KeyM" },
  { id: "flag", code: "KeyU" },
  { id: "emotes", code: "KeyH" },
  { id: "itemNames", code: "KeyZ" },
  { id: "performance", code: "F9" },
  { id: "collision", code: "F8" },
];

/**
 * The actions read as held, not pressed: Move, Sprint and Show item names. None may be on Tab, which walks focus
 * through the page while it is held.
 */
export const HELD_ACTIONS: readonly ActionId[] = [...MOVE_ACTIONS, "run", "itemNames"];
/**
 * Every action's id, in the same order.
 */
export const ACTION_IDS: readonly ActionId[] = ACTIONS.map((action) => action.id);

/**
 * The name Controls shows an action by.
 *
 * @param id The action.
 * @returns Its name.
 * @example
 * actionName("target");
 * // "Target nearest"
 */
export function actionName(id: ActionId): string {
  const slot = ABILITY_SPECS.findIndex((spec) => spec.id === id);
  return slot >= 0 ? say("controls.ability", { slot: String(slot + 1) }) : say(`controls.actions.${id as Exclude<ActionId, AbilityActionId>}`);
}

/**
 * The action of an ability slot.
 *
 * @param slot The slot's index, from 0.
 * @returns The slot's action.
 * @throws RangeError when the bar has no such slot.
 * @example
 * abilityAction(0);
 * // "ability1"
 */
export function abilityAction(slot: number): AbilityActionId {
  const spec = ABILITY_SPECS[slot];
  if (spec === undefined) {
    throw new RangeError(`no ability slot ${slot}`);
  }
  return spec.id as AbilityActionId;
}

/**
 * One row of Controls: one of Move's two sets, a row holding a cap for each direction, as the design draws Move; an
 * action whose key can be chosen; Sprint, its cap beside its Hold or Toggle choice (ADR-116 decisions 8 and 11); the
 * one key shown and never chosen, Escape, named by a key of the language files; or the ability bar's Lock the ability bar choice and Reset ability bar
 * (ADR-121 decisions 2.5 and 3.2, P2.8-T29).
 */
export type ControlRow = { readonly kind: "move"; readonly set: "keys" | "arrows" } | { readonly kind: "action"; readonly id: ActionId } | { readonly kind: "run" } | { readonly kind: "fixed"; readonly nameKey: "controls.menu"; readonly label: string } | { readonly kind: "barLock" } | { readonly kind: "barReset" };

/**
 * Where the ability bar's lock and reset stand among the actions: after Swap bar page, the bar's last key.
 */
const BAR_AFTER: ActionId = "swapPage";

/**
 * Controls' rows in order (ADR-061's amendment for P1.9-T174): Move's two sets, then every other action in the
 * table's order, Sprint as its own row with its Hold or Toggle choice, Lock the ability bar and Reset ability bar after
 * Swap bar page, and Escape last, the one key that cannot be chosen.
 */
export const CONTROL_ROWS: readonly ControlRow[] = [
  { kind: "move", set: "keys" },
  { kind: "move", set: "arrows" },
  ...ACTION_IDS.filter((id) => !isMove(id)).flatMap((id): ControlRow[] => [id === "run" ? { kind: "run" } : { kind: "action", id }, ...(id === BAR_AFTER ? [{ kind: "barLock" } as const, { kind: "barReset" } as const] : [])]),
  { kind: "fixed", nameKey: "controls.menu", label: "Esc" },
];
